import { useState, useEffect } from 'react'
import { type Agent } from '../../../services/agentService'
import { supabase } from '../../../lib/supabase'
import * as Sentry from '@sentry/react'

/**
 * Hook to fetch and manage the full details of the currently active agent.
 * Used to populate the header hover card and navigation links.
 */
export const useCurrentAgent = (agentId: string | null, initialAgent: Agent | null = null) => {
    const [agent, setAgent] = useState<Agent | null>(initialAgent)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        const fetchAgent = async () => {
            if (!agentId) {
                setAgent(null)
                return
            }

            setIsLoading(true)
            setError(null)

            try {
                const { data, error } = await supabase
                    .from('agents')
                    .select('*')
                    .eq('id', agentId)
                    .single()

                if (error) throw error

                setAgent(data as unknown as Agent)
            } catch (err) {
                Sentry.captureException(err, {
                    tags: { hook: 'useCurrentAgent', action: 'fetchAgent' },
                    extra: { agentId }
                })
                setError(err as Error)
                setAgent(null)
            } finally {
                setIsLoading(false)
            }
        }

        fetchAgent()
    }, [agentId])

    return { agent, isLoading, error }
}
